"use client";

import { BookOpen } from "lucide-react";
import { motion } from "framer-motion";
import StatsStrip from "./Stats";

type CurriculumTrack = {
  institution: string;
  subtitle: string;
  modules: string[];
};

const curriculum: {
  indiaTrack: CurriculumTrack;
  usaTrack: CurriculumTrack;
} = {
  indiaTrack: {
    institution: "Mahindra University",
    subtitle: "Foundational and Applied Learning",
    modules: [
      "Programming for Data Science (Python)",
      "Statistics and Probability for Analytics",
      "Data Management and SQL",
      "Data Visualisation and Business Intelligence",
      "Introduction to Machine Learning",
      "Cloud and Data Infrastructure Fundamentals",
      "Applied Projects and Assessments",
      "PGP certification at the end",
    ],
  },

  usaTrack: {
    institution: "Illinois Tech",
    subtitle: "Master of Science in Data Science (STEM)",
    modules: [
      "Advanced Machine Learning",
      "Artificial Intelligence Foundations",
      "Big Data Analytics",
      "Data Engineering and High-Performance Computing",
      "Data Strategy and Governance",
      "Capstone Project/Master’s Thesis",
      "Industry-Oriented Project Work",
      "Masters certification at the end",
    ],
  },
};

const trackImages = {
  india: "/images/7.jpg",
  usa: "/images/8.webp",
};



export default function CurriculumOverview() {
  const tracks = [
    {
      key: "india",
      data: curriculum.indiaTrack,
      semester: "1",
      duration: "1 Semester · ~6 Months · 9 Credits",
      description:
        "Study foundational and applied Data Science modules at Mahindra University.",
      image: trackImages.india,
    },
    {
      key: "usa",
      data: curriculum.usaTrack,
      semester: "2 - 4",
      duration: "3 Semesters · ~18 Months · 24 Credits",
      description:
        "Advance your Data Science expertise through specialised, industry-oriented learning at Illinois Tech.",
      image: trackImages.usa,
    },
  ];

  return (
    <section className="w-full bg-[#fafafa] py-10 lg:pt-24">
      <div className="mx-auto ">

        {/* ================= HEADER ================= */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="text-sm uppercase tracking-[0.2em] text-edept-orange">
            Curriculum
          </p>

          <h2 className="mt-3 text-3xl font-normal leading-tight text-edept-dark sm:text-4xl lg:text-5xl">
            Two credentials, one seamless curriculum.
          </h2>
        </motion.div>


        {/* ================= TRACKS ================= */}
        <div className="mt-16 space-y-20 md:mt-20 md:space-y-24">

          {tracks.map((track) => (
            <div key={track.key} className="contents">

              {/* ================= TRACK ================= */}
              <div
                className="
                  grid
                  gap-8
                  md:grid-cols-[1fr_1.4fr]
                  md:gap-x-12
                  lg:gap-x-14  max-w-7xl px-5 sm:px-8 lg:px-10 mx-auto
                "
              >

                {/* ================= LEFT ================= */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="min-w-0"
                >

                  {/* IMAGE */}
                  <div className="group overflow-hidden rounded-xl border border-neutral-200 bg-neutral-100">
                    <img
                      src={track.image}
                      alt={track.data.institution}
                      className="
                        h-52
                        w-full
                        object-cover
                        transition-transform
                        duration-700
                        ease-out
                        group-hover:scale-[1.03]
                        sm:h-60
                        md:h-52
                        lg:h-48
                      "
                    />
                  </div>


                  {/* SEMESTER */}
                  <p className="mt-7 text-[10px] uppercase tracking-[0.2em] text-[#12539F]">
                    Semester {track.semester}
                  </p>


                  {/* UNIVERSITY */}
                  <h2 className="mt-3 text-3xl leading-tight tracking-tight text-neutral-900 sm:text-4xl">
                    {track.data.institution}
                  </h2>


                  {/* DURATION */}
                  <p className="mt-2 text-sm font-medium text-[#EDA12E]">
                    {track.duration}
                  </p>


                  {/* DESCRIPTION */}
                  <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-neutral-600">
                    {track.description}
                  </p>


                  {/* SUBTITLE */}
                  <div className="mt-6 flex items-start gap-3">
                    <div />
                  </div>

                </motion.div>


                {/* ================= RIGHT / MODULES ================= */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  className="min-w-0"
                >

                  <div className="grid gap-3 sm:grid-cols-2">

                    {track.data.modules.map((module, index) => {

                      const isCredential =
                        module.toLowerCase().includes("certification");

                      return (
                        <div
                          key={`${track.key}-${index}`}
                          className={`
                            flex
                            min-h-[82px]
                            items-start
                            gap-3
                            rounded-xl
                            border
                            px-5
                            py-4
                            transition-all
                            duration-300
                            ${
                              isCredential
                                ? "border-[#12539F]/30 bg-[#12539F]/[0.04]"
                                : "border-neutral-200 bg-white hover:border-[#12539F]/50 hover:shadow-sm"
                            }
                          `}
                        >

                          {/* ICON */}
                          <BookOpen
                            size={18}
                            strokeWidth={1.7}
                            className="mt-0.5 shrink-0 text-[#12539F]"
                          />


                          {/* MODULE */}
                          <div className="min-w-0">

                            <p
                              className={`
                                text-[15px]
                                leading-[1.45]
                                ${
                                  isCredential
                                    ? "font-medium text-neutral-900"
                                    : "text-neutral-700"
                                }
                              `}
                            >
                              {module}
                            </p>


                            {isCredential && (
                              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.15em] text-[#12539F]">
                                Credential
                              </p>
                            )}

                          </div>

                        </div>
                      );
                    })}

                  </div>

                </motion.div>

              </div>


              {/* =================================================
                  INSERT NEW COMPONENT ONLY AFTER INDIA TRACK
              ================================================= */}
              {track.key === "india" && (
                <StatsStrip />
              )}

            </div>
          ))}

        </div>

      </div>
    </section>
  );
}